<template>
  <div>
    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)"><div class="notice__body">{{ error }}</div></div>

    <div v-if="issued" class="card credentials__secret">
      <h2 class="card__title">{{ t("editor.newSecretTitle") }}</h2>
      <p class="strong">{{ t("editor.secretOnce") }}</p>
      <dl class="kv">
        <dt>{{ t("editor.clientId") }}</dt>
        <dd><code>{{ issued.client_id }}</code></dd>
        <dt>{{ t("editor.clientSecret") }}</dt>
        <dd><code class="break-all">{{ issued.client_secret }}</code></dd>
      </dl>
      <p v-if="issued.old_credential_expires_at || issued.previous_expires_at" class="small muted">
        {{ t("editor.overlap", { when: formatDate(issued.old_credential_expires_at || issued.previous_expires_at) }) }}
      </p>
      <div class="row" style="margin-top: var(--space-3)">
        <button class="btn btn--secondary btn--sm" @click="copySecret">{{ copied ? t("common.copied") : t("editor.copySecret") }}</button>
        <button class="btn btn--ghost btn--sm" @click="issued = null">{{ t("editor.storedIt") }}</button>
      </div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("editor.credentials") }}</h2>
      <p class="card__sub">{{ t("editor.credentialsLede") }}</p>
      <div v-if="credentials.length" class="table__wrap">
        <table class="table">
          <thead>
            <tr>
              <th>{{ t("editor.clientId") }}</th>
              <th>{{ t("editor.clientSecret") }}</th>
              <th>{{ t("editor.environment") }}</th>
              <th>{{ t("common.status") }}</th>
              <th>{{ t("editor.lastUsed") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in credentials" :key="c.id">
              <td><code class="xs">{{ c.client_id }}</code></td>
              <td class="muted mono xs">…{{ c.secret_last4 }}</td>
              <td><span :class="['badge', c.environment === 'live' ? 'badge--brand' : '']">{{ c.environment }}</span></td>
              <td>
                <span v-if="c.revoked_at" class="badge badge--danger">{{ t("editor.revoked") }}</span>
                <span v-else-if="c.previous_expires_at && new Date(c.previous_expires_at) > new Date()" class="badge badge--warn">
                  {{ t("editor.rotating") }}
                </span>
                <span v-else class="badge badge--success">{{ t("editor.active") }}</span>
              </td>
              <td class="muted small">{{ c.last_used_at ? formatDate(c.last_used_at) : t("common.never") }}</td>
              <td>
                <div v-if="!c.revoked_at" class="row">
                  <button class="btn btn--ghost btn--sm" :disabled="busy" @click="rotate(c)">{{ t("editor.rotate") }}</button>
                  <button class="btn btn--ghost btn--sm" :disabled="busy" @click="revoke(c)">{{ t("editor.revoke") }}</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="muted small">{{ t("editor.noCredentials") }}</p>

      <div class="row row--wrap" style="margin-top: var(--space-4)">
        <button class="btn" :disabled="busy || archived" @click="create('dev')">{{ t("editor.generate") }}</button>
        <button class="btn btn--secondary" :disabled="busy || !canIssueLive" @click="create('live')">{{ t("editor.generateLive") }}</button>
        <span v-if="!canIssueLive" class="small muted">{{ t("editor.liveHint") }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../../lib/api";
import { useErrorText, formatDate } from "../../lib/errors";

const props = defineProps({
  app: { type: Object, required: true },
  credentials: { type: Array, default: () => [] }
});
const emit = defineEmits(["changed"]);
const { t } = useI18n();
const errorText = useErrorText();

const busy = ref(false);
const error = ref("");
const issued = ref(null);
const copied = ref(false);

const archived = computed(() => props.app.status === "archived");
const canIssueLive = computed(() => ["published", "private"].includes(props.app.status));

async function run(fn) {
  busy.value = true;
  error.value = "";
  try {
    await fn();
    emit("changed");
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

const create = (environment) => run(async () => {
  issued.value = await api.createCredential(props.app.id, { environment });
});

const rotate = (c) => {
  if (!window.confirm(t("editor.rotateConfirm", { id: c.client_id }))) return;
  return run(async () => {
    issued.value = await api.rotateCredential(props.app.id, c.id);
  });
};

const revoke = (c) => {
  if (!window.confirm(t("editor.revokeConfirm", { id: c.client_id }))) return;
  return run(() => api.revokeCredential(props.app.id, c.id));
};

async function copySecret() {
  try {
    await navigator.clipboard.writeText(issued.value.client_secret);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  } catch (e) {
    error.value = t("errors.clipboard");
  }
}
</script>

<style scoped>
.credentials__secret { border-color: var(--amber-500); }
</style>
